import React, { useState, useCallback } from 'react';
import { LessonCarousel } from './LessonCarousel';
import { QuizView } from './QuizView';
import { QuizResults } from './QuizResults';
import type { Level } from '../../types/course';

type Phase = 'lesson' | 'quiz' | 'results';

interface Props {
  level: Level;
  sectionId: string;
  hasNextLevel: boolean;
  onComplete: (levelId: string, sectionId: string, quizScore: number, quizTotal: number) => void;
  onBack: () => void;
  onNext: () => void;
}

export const LevelView: React.FC<Props> = ({
  level,
  sectionId,
  hasNextLevel,
  onComplete,
  onBack,
  onNext,
}) => {
  const [phase, setPhase] = useState<Phase>('lesson');
  const [quizScore, setQuizScore] = useState(0);
  const [quizTotal, setQuizTotal] = useState(0);

  const handleLessonComplete = useCallback(() => {
    setPhase('quiz');
  }, []);

  const handleQuizComplete = useCallback(
    (score: number, total: number) => {
      setQuizScore(score);
      setQuizTotal(total);
      setPhase('results');
      // Always save progress regardless of score
      onComplete(level.id, sectionId, score, total);
    },
    [level.id, sectionId, onComplete]
  );

  const handleRetry = useCallback(() => {
    setPhase('quiz');
    setQuizScore(0);
    setQuizTotal(0);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[var(--clue-border-mid)]">
        <button
          onClick={onBack}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--clue-bg-secondary)] transition-colors"
          aria-label="Back to course"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--clue-text-secondary)]">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="min-w-0">
          <h2 className="text-base font-semibold font-serif text-[var(--clue-text-primary)] truncate">
            {level.title}
          </h2>
          <p className="text-xs text-[var(--clue-text-tertiary)]">
            {phase === 'lesson' ? 'Lesson' : phase === 'quiz' ? 'Quiz' : 'Results'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {phase === 'lesson' && (
          <LessonCarousel screens={level.screens} onComplete={handleLessonComplete} />
        )}
        {phase === 'quiz' && (
          <QuizView
            key={`quiz-${quizScore}-${quizTotal}`}
            questions={level.quiz}
            onComplete={handleQuizComplete}
          />
        )}
        {phase === 'results' && (
          <QuizResults
            score={quizScore}
            total={quizTotal}
            levelTitle={level.title}
            hasNext={hasNextLevel}
            onNext={onNext}
            onRetry={handleRetry}
          />
        )}
      </div>
    </div>
  );
};
